import * as Phaser from "phaser-ce"
import { CCUtils } from "../../../../common/CCUtils"
import { ActivityKey } from "../../../../shared/models/ActivityKey"
import { Buttons } from "../../../generic-sprites/Buttons"
import { loadGenericAssets } from "../../../generic-sprites/GenericAssets"
import { ColorUtils } from "../../../utils/ColorUtils"
import { PhaserUtils } from "../../../utils/PhaserUtils"
import { Scale } from "../../../utils/Scale"
import { MakeVaperPartsStore } from "../MakeVaperPartsStore"
import { loadSharedAssets, MakeVaperImages } from "../MakeYourOwnVaperAssets"
import { MakeVaperContentKind } from "../types/MakeVaperScreenContentTypes"
import { MakeVaperScreenType, MakeVaperSpudPosition, MakeVaperTitleTextPosition, MakeVaperWhiteBoxPosition } from "../types/MakeVaperScreenValueTypes"
import { MakeVaperActivityScreens, MakeVaperPart } from "../types/MakeYourOwnVaperTypes"
import { MakeVaperScreenBuilder } from "../utils/MakeVaperScreenBuilder"
import { loadAssets, MakeVaperPt1Audio } from "./MakeYourOwnVaperPartOneAssets"


export class MakeYourOwnVaperPartOneState extends Phaser.State {
    private vaperPartsGroup: Phaser.Group
    private ecigGroup: Phaser.Group
    private screen: MakeVaperScreenBuilder

    async preload() {
        loadGenericAssets(this.game)
        loadSharedAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        MakeVaperPartsStore.newInstance()
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setBackgroundColorLightGray(this.game)
        if (!PhaserUtils.loggedIn() && !CCUtils.debug) {
            PhaserUtils.notLoggedInMessage(this.game)
            return
        }

        this.screen = MakeVaperScreenBuilder.create(this.game, ActivityKey.MAKE_YOUR_OWN_VAPER_PT_1, this.allScreens)

        this.vaperPartsGroup = this.game.add.group()
        this.ecigGroup = this.game.add.group()
    }

    get allScreens(): Array<MakeVaperActivityScreens> {
        return [this.introScreen, MakeYourOwnVaperPartOneState.instructionScreen, this.optionsScreen, this.reviewScreen]
    }

    private get introScreen(): MakeVaperActivityScreens {
        return {
            type: MakeVaperScreenType.TIMED,
            titleImage: true,
            screenAudio: MakeVaperPt1Audio.INTRO,
            screenContent:
                [
                    {
                        kind: MakeVaperContentKind.LETTERED_TEXT,
                        delay: 2500,
                        text: "Show Mr. Spud as a vaper."
                    },
                    {
                        kind: MakeVaperContentKind.FUNCTION,
                        delay: 4000,
                        function: () => this.screen.showEcig(true)
                    },
                    {
                        kind: MakeVaperContentKind.LETTERED_TEXT,
                        delay: 6000,
                        text: "See what your classmates think of kids who vape."
                    },
                    {
                        kind: MakeVaperContentKind.LETTERED_TEXT,
                        delay: 8000,
                        text: "See how your ideas compare to theirs."
                    }
                ]
        }
    }

    private static get instructionScreen(): MakeVaperActivityScreens {
        return {
            type: MakeVaperScreenType.TIMED,
            spudTitle: true,
            whiteBox: MakeVaperWhiteBoxPosition.LEFT,
            screenAudio: MakeVaperPt1Audio.INSTRUCTIONS,
            screenContent:
                [
                    {kind: MakeVaperContentKind.LETTERED_TEXT, delay: 0, text: "See words that may or may not describe kids who vape."},
                    {kind: MakeVaperContentKind.LETTERED_TEXT, delay: 7000, text: "Answer \"Yes\" or \"No\" to each word."},
                    {kind: MakeVaperContentKind.LETTERED_TEXT, delay: 13500, text: "Mr. Spud will change automatically."}
                ]
        }
    }

    private get optionsScreen(): MakeVaperActivityScreens {
        return {
            type: MakeVaperScreenType.OPTION,
            spudTitle: true,
            whiteBox: MakeVaperWhiteBoxPosition.LEFT,
            titleText: {
                text: "Do you think kids who vape are:",
                position: MakeVaperTitleTextPosition.LEFT
            },
            screenContent: [
                {
                    kind: MakeVaperContentKind.OPTION,
                    part: MakeVaperPart.SMART,
                    audio: MakeVaperPt1Audio.SMART,
                    text: "Smart",
                    yesSprite: this.createOptionSprite(MakeVaperImages.SMART, Scale.x(0.649), Scale.y(0.309)),
                    noSprite: this.createOptionSprite(MakeVaperImages.DUNCE, Scale.x(0.681), Scale.y(0.091)),
                },
                {
                    kind: MakeVaperContentKind.OPTION,
                    part: MakeVaperPart.COOL,
                    audio: MakeVaperPt1Audio.COOL,
                    text: "Cool",
                    yesSprite: this.createOptionSprite(MakeVaperImages.SUNGLASSES, Scale.x(0.662), Scale.y(0.478)),
                    noSprite: this.createOptionSprite(MakeVaperImages.BIB, Scale.x(0.693), Scale.y(0.706)),
                },
                {
                    kind: MakeVaperContentKind.OPTION,
                    part: MakeVaperPart.POPULAR,
                    audio: MakeVaperPt1Audio.POPULAR,
                    text: "Popular",
                    yesSprite: this.createOptionSprite(MakeVaperImages.HAPPY_POTATOES, Scale.x(0.563), Scale.y(0.752)),
                    noSprite: this.createOptionSprite(MakeVaperImages.SAD_POTATOES, Scale.x(0.831), Scale.y(0.124)),
                },
                {
                    kind: MakeVaperContentKind.OPTION,
                    part: MakeVaperPart.EXCITING,
                    audio: MakeVaperPt1Audio.EXCITING,
                    text: "Exciting",
                    yesSprite: this.createOptionSprite(MakeVaperImages.EXCITED_POTATOES, Scale.x(0.561), Scale.y(0.107)),
                    noSprite: this.createOptionSprite(MakeVaperImages.BORED_POTATOES, Scale.x(0.554), Scale.y(0.163)),
                },
                {
                    kind: MakeVaperContentKind.OPTION,
                    part: MakeVaperPart.ATHLETIC,
                    audio: MakeVaperPt1Audio.ATHLETIC,
                    text: "Athletic",
                    yesSprite: this.createOptionSprite(MakeVaperImages.BUFF_ARMS, Scale.x(0.558), Scale.y(0.542)),
                    noSprite: this.createOptionSprite(MakeVaperImages.WEAK_ARMS, Scale.x(0.583), Scale.y(0.576)),
                }
            ]
        }
    }

    private get reviewScreen(): MakeVaperActivityScreens {
        return {
            type: MakeVaperScreenType.TIMED,
            spudTitle: true,
            screenAudio: MakeVaperPt1Audio.FINISHED_VAPER,
            centerContent: true,
            screenContent: [
                {
                    kind: MakeVaperContentKind.FUNCTION,
                    delay: 0,
                    function: () => {
                        MakeVaperPartsStore.instance.saveToFirestore()
                        this.screen.spud.setSpudPosition(MakeVaperSpudPosition.CENTER)
                        this.screen.forEachVaperPart(sprite => Buttons.undoSpriteButton(sprite))
                    }
                }

            ]
        }
    }

    private createOptionSprite = (asset: string, x: number, y: number) =>
        () => MakeVaperScreenBuilder.createOptionSprite(this.game, this.vaperPartsGroup, asset, x, y)
}
